import { useCallback, useState } from 'react';
import { useShowContext } from 'framework/show';
import { useListContext } from 'framework/list';
import useEmployeesDashboardService from '../useEmployeesDashboardService';
import { ErrorMessage, SuccessMessage, useMessageService } from 'framework/ui/Message';
import { useCoordinatorManagement } from './useCoordinatorManagement';
import { DashboardEmployee, RetentionRiskFollowUp, RetentionRiskIssue } from '../types';
import { DELEGATED_EMPLOYEE_ZONES_ALIASES, EMPLOYEE_ZONES, RETENTION_RISK_ISSUE_STATUS } from '../constants';
import { EMPLOYEE_ZONES_ALIASES } from 'domain/shared/employee';
import { useTimelineRefresh } from '../List/EmployeeDashboardItem/TimelineRefreshContext';

interface UpdateIssuesRequest {
  issues: Partial<RetentionRiskIssue>[];
  isClosedIssuesPostponed: boolean;
  postponeInformation?: RetentionRiskFollowUp;
}

interface ChangeZoneRequest extends UpdateIssuesRequest {
  targetZone: string;
}

interface UpdateIssuesResponse {
  issues?: RetentionRiskIssue[];
  priority?: DashboardEmployee['retentionRiskPriority'];
}

interface HealthcheckValues {
  [key: string]: any;
}

interface FollowUpFormData {
  description: string;
  followUpDate: string;
}

export const useEmployeeDashboardModals = () => {
  const { data, setData } = useShowContext<DashboardEmployee>();
  const { deleteFromList } = useListContext();
  const [targetZone, setTargetZone] = useState<string | undefined>(undefined);
  const [followUpForUpdate, setFollowUpForUpdate] = useState<Nullable<RetentionRiskFollowUp>>(null);
  const { refreshTimeline } = useTimelineRefresh();

  const {
    updateIssues,
    changeZonePriority,
    updateHealthcheck,
    getValidationHelthchekSchema: getValidationHealthcheckSchema,
    createFollowUp,
    updateFollowUp,
    getFollowUpValidation,
    appendProjectNamesToIssuesDescription,
  } = useEmployeesDashboardService();
  const { addMessage } = useMessageService();
  const { coordinatorOptions } = useCoordinatorManagement();

  const employeeData = data?.getData();
  const riskId = employeeData?.retentionRiskId;
  const employeeAlias = employeeData?.employeeAlias;
  const currentZone = data?.currentZone as keyof typeof EMPLOYEE_ZONES;

  const updateEmployeeData = useCallback(
    (updates: Partial<DashboardEmployee>) => {
      setData!({
        ...data.getData(),
        ...updates,
      });
    },
    [data, setData],
  );

  const updateIssuesAndPriority = useCallback(
    async (response: UpdateIssuesResponse) => {
      const activeIssues =
        response.issues?.filter(
          (element: RetentionRiskIssue) => element.status === RETENTION_RISK_ISSUE_STATUS.ACTIVE,
        ) || [];
      const enrichedIssues = await appendProjectNamesToIssuesDescription(activeIssues);

      updateEmployeeData({
        retentionRiskIssues: enrichedIssues,
        retentionRiskPriority: response.priority,
      });
    },
    [appendProjectNamesToIssuesDescription, updateEmployeeData],
  );

  const handleChangeZone = useCallback((zone: string) => {
    setTargetZone(zone);
  }, []);

  const handleIssuesSubmit = useCallback(
    async (req: UpdateIssuesRequest) => {
      try {
        const response = await updateIssues(riskId, req);

        const updatedRetentionRiskIssues = response.issues?.filter(
          (element: RetentionRiskIssue) => element.status === RETENTION_RISK_ISSUE_STATUS.ACTIVE,
        );

        if (
          response.priority === DELEGATED_EMPLOYEE_ZONES_ALIASES.DELEGATED &&
          updatedRetentionRiskIssues?.length === 0 &&
          deleteFromList
        ) {
          deleteFromList(riskId);
        } else {
          await updateIssuesAndPriority(response);
        }
      } catch {
        addMessage(new ErrorMessage('Update issue error'));
      }
    },
    [riskId, updateIssues, updateIssuesAndPriority, addMessage, deleteFromList],
  );

  const handleHealthcheckSubmit = useCallback(
    async (values: HealthcheckValues) => {
      try {
        const response = await updateHealthcheck(riskId, employeeAlias, values);
        addMessage(new SuccessMessage('Healthcheck updated successfully'));
        return response;
      } catch {
        addMessage(new ErrorMessage('Healthcheck update error'));
        return undefined;
      }
    },
    [riskId, employeeAlias, updateHealthcheck, addMessage],
  );

  const handleChangeZoneSubmit = useCallback(
    async (req: ChangeZoneRequest) => {
      try {
        const { targetZone: targetPriority, ...rest } = req;

        await changeZonePriority(riskId, { targetPriority });
        const issuesResponse = await updateIssues(riskId, rest);

        await updateIssuesAndPriority(issuesResponse);

        setTargetZone(undefined);
        addMessage(new SuccessMessage('Zone changed successfully'));
      } catch {
        addMessage(new ErrorMessage('Change zone error'));
      }
    },
    [riskId, updateIssues, changeZonePriority, updateIssuesAndPriority, addMessage],
  );

  const handleFollowUpFormSubmit = useCallback(
    async (requestData: FollowUpFormData) => {
      if (followUpForUpdate) {
        return updateFollowUp(riskId, followUpForUpdate.id, {
          description: requestData.description,
          followUpDate: requestData.followUpDate,
        });
      }
      return createFollowUp(riskId, requestData);
    },
    [followUpForUpdate, riskId, updateFollowUp, createFollowUp],
  );

  const handleFollowUpAfterSubmit = useCallback(
    (response: RetentionRiskFollowUp) => {
      if (!response?.id) {
        return;
      }

      const employeeInfo = data.getData();
      const followUps = employeeInfo.retentionRiskFollowUps || [];

      const updatedFollowUps = followUps
        .filter((item: RetentionRiskFollowUp) => item.id !== response.id)
        .concat(response)
        .sort(
          (a: RetentionRiskFollowUp, b: RetentionRiskFollowUp) =>
            new Date(a.followUpDate).getTime() - new Date(b.followUpDate).getTime(),
        );

      updateEmployeeData({ retentionRiskFollowUps: updatedFollowUps });
      setFollowUpForUpdate(null);
    },
    [data, updateEmployeeData],
  );

  const handleHealthcheckAfterSubmit = useCallback(
    (response: { lastHealthcheckDate?: string }) => {
      if (response?.lastHealthcheckDate) {
        updateEmployeeData({
          retentionRiskLastHealthcheck: response.lastHealthcheckDate,
        });
      }
      refreshTimeline.current?.();
    },
    [updateEmployeeData, refreshTimeline],
  );

  const otherZones = Object.keys(EMPLOYEE_ZONES_ALIASES).filter(zone => zone !== currentZone);

  const zoneOptions = otherZones.map(zone => ({
    key: `zone-${zone}`,
    label: EMPLOYEE_ZONES[zone as keyof typeof EMPLOYEE_ZONES],
    onClick: () => handleChangeZone(zone),
  }));

  const modalCallbacks = {
    issues: {
      onSubmit: handleIssuesSubmit,
      onClose: () => {},
      afterSubmit: {
        execute: () => refreshTimeline.current?.(),
      },
    },
    followUp: {
      onSubmit: handleFollowUpFormSubmit,
      onClose: () => setFollowUpForUpdate(null),
      getValidationSchema: getFollowUpValidation,
      afterSubmit: {
        execute: handleFollowUpAfterSubmit,
      },
    },
    healthcheck: {
      onSubmit: handleHealthcheckSubmit,
      onClose: () => {},
      getValidationSchema: getValidationHealthcheckSchema,
      initialData: {
        date: employeeData?.retentionRiskLastHealthcheck || '',
        description: '',
      },
      afterSubmit: {
        execute: handleHealthcheckAfterSubmit,
      },
    },
    changeZone: {
      onSubmit: async (changeZoneData: ChangeZoneRequest) => {
        await handleChangeZoneSubmit(changeZoneData);
      },
      onClose: () => setTargetZone(undefined),
      onOpen: (modalData: any) => {
        if (modalData?.targetZone) {
          setTargetZone(modalData.targetZone);
        }
      },
    },
  };

  const globalData = {
    employeeData,
    riskId,
    targetZone,
    followUpForUpdate,
    predefinedIssues: data?.retentionRiskIssues || [],
    coordinatorOptions,
    zoneOptions,
  };

  return {
    modalCallbacks,
    globalData,
    setFollowUpForUpdate,
    handleFollowUpAfterSubmit,
    handleHealthcheckAfterSubmit,
  };
};
